import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StoreService } from './store.service';
import { Observable } from 'rxjs';


// Define interfaces for API responses to ensure type safety
interface ConnectionTokenResponse {
   secret: string;
}

interface SimulatedConfigResponse {
   IsStripeTestMode: boolean;
}

interface PaymentIntentResponse {
   paymentIntentId: string | null;
   clientSecret: string;
   message: string;
   isSuccess: boolean;
}

export interface ReaderDto {
   id: string;
   label: string;
   status: string;
   deviceType: string;
   ipAddress: string | null;
}

export interface TerminalPaymentResult {
   paymentIntentId: string;
   clientSecret: string;
   message: string;
   tipAmount: number;
   isSuccess: boolean;
   requiresManualEntry?: boolean;
   paymentId: number;
   transactionId: number;
   paymentSourceTypeId: number;
   isSignatureCaptureEnabled: boolean;
   isEMVTerminal: boolean;
   isAuthOnly: boolean;
   totalPaidAmount: number;
   totalPendingPayment: number;
}

@Injectable({
   providedIn: 'root'
})
export class StripePaymentService {

   private apiBaseUrl: string;
   private backendUrls = {
      connectionToken: '/kioskapi/api/stripeterminal/GetReaderToken',
      createPaymentIntent: '/payment/stripe/create-payment-intent',
      capturePaymentIntent: '/payment/stripe/capture-payment-intent',
      savePaymentResult: '/kioskapi/api/checkout/PaymentStripe',
      terminalConfig: '/payment/stripe/terminalconfig',
      getCustomer: '/payment/stripe/get-customer',
      config: '/payment/stripe/config',
      getDefaultReader: '/payment/stripe/terminal/get-default-stripe-reader-for-register',
      terminalProcessPayment: '/kioskapi/api/stripeterminal/ProcessPayment',
      terminalProcessKioskPayment: '/kioskapi/api/stripeterminal/ProcessKioskPayment',
      terminalCancelAction: '/kioskapi/api/stripeterminal/CancelAction',
      terminalCancelKioskPayment: '/kioskapi/api/stripeterminal/CancelKioskPayment'
   };

   constructor(private httpClient: HttpClient, private storeService: StoreService) {
      this.apiBaseUrl = `${window.location.origin}`;
   }

   getStripePaymentConfiguration(): Observable<any> {
      return this.httpClient.get(`${this.apiBaseUrl}${this.backendUrls.config}`);
   }

   public createPaymentIntent(amount: number, currency: string, isAutomaticPaymentMethodsEnabled: boolean, customerId: string, locationId: number): Observable<PaymentIntentResponse> {
      return this.httpClient.post<PaymentIntentResponse>(`${this.apiBaseUrl}${this.backendUrls.createPaymentIntent}`, {
         amount: amount,
         currency: currency,
         CustomerId: customerId,
         LocationId: locationId,
         IsAutomaticPaymentMethodsEnabled: isAutomaticPaymentMethodsEnabled,
         transactionId: this.storeService.shoppingCart.transactionId
      });
   }

   public fetchConnectionToken(): Observable<ConnectionTokenResponse> {
      return this.httpClient.post<ConnectionTokenResponse>(`${this.apiBaseUrl}${this.backendUrls.connectionToken}`,
         {
            locationId: this.storeService.kioskSettings.locationId || 1
         });
   }

   public getStripeTerminalConfig(): Observable<SimulatedConfigResponse> {
      return this.httpClient.get<SimulatedConfigResponse>(`${this.apiBaseUrl}${this.backendUrls.terminalConfig}`);
   }

   public capturePaymentIntent(paymentIntentId: string, amount: number, currency: string): Observable<any> {
      return this.httpClient.post(`${this.apiBaseUrl}${this.backendUrls.capturePaymentIntent}`, {
         PaymentIntentId: paymentIntentId,
         Amount: amount,
         Currency: currency,
      });
   }

   public savePaymentResult(paymentIntentId: string, amountToPay: number): Observable<any> {
      const transactionId = this.storeService.shoppingCart.transactionId;
      const orderId = this.storeService.shoppingCart.orderId;
      const paymentSourceTypeId = 1017;
      let invoice = `${this.storeService.kioskSettings.tenantName}${orderId}`;
      let invoiceStr = invoice;
      if (invoice.length > 9) {
         invoiceStr = invoice.slice(invoice.length - 9, invoice.length);
      }

      return this.httpClient.post(`${this.apiBaseUrl}${this.backendUrls.savePaymentResult}`, {
         StripePaymentId: paymentIntentId,
         TransactionId: transactionId,
         OrderId: this.storeService.shoppingCart.orderId,
         GuestId: this.storeService.shoppingCart.guestId,
         GuestName: this.storeService.shoppingCart.guestName,
         GuestCallName: this.storeService.shoppingCart.guestName,
         GuestEmail: this.storeService.shoppingCart.guestEmail,
         PaymentAmount: amountToPay,
         PaymentSourceTypeId: paymentSourceTypeId,
         Invoice: invoiceStr
      });
   }

   public processPayment(stripePaymentId: string): Observable<any> {
      const apiUrl = `${this.apiBaseUrl}/KioskAPI/api/Checkout/ProcessPayment`;
      const body = { stripePaymentId: stripePaymentId, transactionId: this.storeService.shoppingCart.transactionId };

      return this.httpClient.post(apiUrl, body);
   }

   getCustomer(guestName: string, guestEmail: string, locationId: number): Observable<any> {

      return this.httpClient.post(`${this.apiBaseUrl}${this.backendUrls.getCustomer}`, {
         CustomerName: guestName,
         CustomerEmail: guestEmail,
         LocationId: locationId
      });
   }

   public getDefaultReaderForRegister(registerId: number): Observable<ReaderDto> {
      return this.httpClient.post<ReaderDto>(
         `${this.apiBaseUrl}${this.backendUrls.getDefaultReader}`,
         { RegisterId: registerId }
      );
   }

   public processStripeTerminalPayment(request: {
      paymentIntentId: string;
      readerId: string;
      locationId: number;
      transactionId: number;
      guestId: number;
      guestName: string;
      guestCallName: string;
      paymentAmount: number;
      paymentSourceTypeId: number;
   }): Observable<TerminalPaymentResult> {
      return this.httpClient.post<TerminalPaymentResult>(
         `${this.apiBaseUrl}${this.backendUrls.terminalProcessPayment}`,
         {
            PaymentIntentId: request.paymentIntentId,
            ReaderId: request.readerId,
            LocationId: request.locationId,
            TransactionId: request.transactionId,
            GuestId: request.guestId,
            GuestName: request.guestName,
            GuestCallName: request.guestCallName,
            PaymentAmount: request.paymentAmount,
            PaymentSourceTypeId: request.paymentSourceTypeId
         }
      );
   }

   public processKioskTerminalPayment(request: {
      registerId: number;
      locationId: number;
      transactionId: number;
      guestId: number;
      guestName: string;
      guestCallName: string;
      paymentAmount: number;
      paymentSourceTypeId: number;
   }): Observable<TerminalPaymentResult> {
      return this.httpClient.post<TerminalPaymentResult>(
         `${this.apiBaseUrl}${this.backendUrls.terminalProcessKioskPayment}`,
         {
            RegisterId: request.registerId,
            LocationId: request.locationId,
            TransactionId: request.transactionId,
            GuestId: request.guestId,
            GuestName: request.guestName,
            GuestCallName: request.guestCallName,
            PaymentAmount: request.paymentAmount,
            PaymentSourceTypeId: request.paymentSourceTypeId
         }
      );
   }

   public cancelReaderAction(readerId: string, locationId: number): Observable<any> {
      return this.httpClient.post(
         `${this.apiBaseUrl}${this.backendUrls.terminalCancelAction}`,
         { ReaderId: readerId, LocationId: locationId }
      );
   }

   public cancelKioskPayment(registerId: number, locationId: number): Observable<any> {
      return this.httpClient.post(
         `${this.apiBaseUrl}${this.backendUrls.terminalCancelKioskPayment}`,
         { RegisterId: registerId, LocationId: locationId }
      );
   }
}